import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Upload, X, Loader2, ImagePlus, AlertCircle, Camera } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { useUploadLimitMb } from "@/lib/limits";

const ACCEPT = "image/*";

/**
 * Reusable image uploader.
 * - Accepts image/*, up to the platform limit (`max_upload_size_mb`, lib/limits)
 * - Posts to /api/uploads/image (multipart) with `purpose`
 * - Calls onUploaded({ file_id, url }) on success
 *
 * **`preview` is local state, and that is the whole reason `resetAfterUpload`
 * exists.** Used as a slot whose `value` stays `null` — the "add another photo"
 * tile in JobPhotoGrid — the component kept showing the photo it had just
 * uploaded, because the sync effect below only runs when `value` *changes* and
 * `null === null`. The list grew by one item and the slot still held the image:
 * one camera capture, two thumbnails, and only one of them real. The parent
 * owns the photo the moment `onUploaded` fires, so a slot hands it over and
 * goes empty again.
 *
 * Props:
 *   value: existing url to preview (optional)
 *   purpose: "profile_photo" | "landing_logo" | "landing_portfolio" | "job_photo"
 *   onUploaded: ({file_id, url}) => void          single-file callers
 *   onUploadedMany: ([{file_id, url}, …]) => void  used instead when `multiple`
 *   onCleared: () => void
 *   onExpand: () => void   makes the preview itself tappable (lightbox)
 *   aspect: "square" | "wide" | "auto"
 *   label: button text
 *   multiple: allow picking several files in one go (galleries only)
 *   maxFiles: how many of that selection fit; the rest are refused out loud
 *   resetAfterUpload: empty the tile once the parent has the url
 *   testid: data-testid prefix
 */
export default function ImageUploader({
  value,
  purpose = "profile_photo",
  onUploaded,
  onUploadedMany,
  onCleared,
  onExpand,
  aspect = "square",
  label = null,
  multiple = false,
  maxFiles = 1,
  resetAfterUpload = false,
  testid = "image-uploader",
}) {
  const { t } = useTranslation();
  const maxMb = useUploadLimitMb();
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const [preview, setPreview] = useState(value || null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setPreview(value || null);
  }, [value]);

  const pick = () => inputRef.current?.click();

  const uploadOne = async (file) => {
    const form = new FormData();
    form.append("file", file);
    form.append("purpose", purpose);
    const { data } = await api.post("/uploads/image", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  };

  // A second selection while the first is still in flight is either a double
  // `change` — Android's camera path has been known to fire one — or an
  // impatient double tap. Both end as two uploads of the same photo.
  const busy = useRef(false);

  const handleFiles = async (fileList) => {
    if (busy.current) return;
    setError("");
    const picked = Array.from(fileList || []);
    if (!picked.length) return;

    const room = multiple ? Math.max(0, maxFiles) : 1;
    const files = picked.slice(0, room);
    if (!files.length) return;

    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        setError(t("uploader.pickImage"));
        return;
      }
      if (file.size > maxMb * 1024 * 1024) {
        setError(t("uploader.maxSize", { mb: maxMb }));
        return;
      }
    }

    // Local preview while uploading — the first of the batch stands for it.
    const objUrl = URL.createObjectURL(files[0]);
    busy.current = true;
    setPreview(objUrl);
    setUploading(true);

    const uploaded = [];
    let failure = null;
    try {
      for (const file of files) {
        uploaded.push(await uploadOne(file));
      }
    } catch (e) {
      failure = e;
    }
    busy.current = false;
    setUploading(false);

    // Whatever did land is handed over even if a later file failed: losing
    // three good uploads because the fourth was rejected is the worse bug.
    if (uploaded.length) {
      if (onUploadedMany) onUploadedMany(uploaded);
      else if (onUploaded) onUploaded(uploaded[0]);
    }

    if (failure) {
      setError(errorMessage(failure, t("uploader.uploadFailed")));
      setPreview(resetAfterUpload ? null : value || null);
    } else {
      if (picked.length > files.length) {
        // N8. Pluralised: `count` is how many of the selection fit, and at a
        // cap of eight the overflow is most often one photo — "Only 1 more
        // photos fit" was the sentence a person met at the exact moment they
        // needed to trust it.
        setError(t("uploader.tooMany", { count: files.length }));
      }
      setPreview(resetAfterUpload ? null : uploaded[uploaded.length - 1].url);
    }
    // Last, not in a `finally`: revoking before the real url is in state left
    // the tile pointing at a dead blob for a frame.
    URL.revokeObjectURL(objUrl);
  };

  // The same file has to be pickable twice in a row — remove a photo, pick it
  // again — and a file input whose value still holds that path fires no change.
  const onInputChange = async (e) => {
    const input = e.target;
    await handleFiles(input.files);
    input.value = "";
  };

  const clear = () => {
    setPreview(null);
    setError("");
    if (onCleared) onCleared();
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer?.files;
    if (files?.length) handleFiles(files);
  };

  const aspectClass =
    aspect === "square" ? "aspect-square"
    : aspect === "wide" ? "aspect-[16/10]"
    : "min-h-[160px]";

  return (
    <div className="w-full" data-testid={testid}>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple={multiple}
        className="hidden"
        onChange={onInputChange}
        data-testid={`${testid}-input`}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onInputChange}
        data-testid={`${testid}-camera`}
      />

      {preview ? (
        <div className="relative group">
          {/* The image is its own tap target when the caller offers one; the
              Replace/Remove buttons sit above it and are never swallowed. */}
          {onExpand && !uploading ? (
            <button
              type="button"
              onClick={onExpand}
              aria-label={t("uploader.expandAlt")}
              className={`block w-full ${aspectClass} rounded-md border border-sand overflow-hidden ring-hive`}
              data-testid={`${testid}-expand`}
            >
              <img
                src={mediaUrl(preview)}
                alt={t("uploader.previewAlt")}
                className="w-full h-full object-cover"
                data-testid={`${testid}-preview`}
              />
            </button>
          ) : (
            <img
              src={mediaUrl(preview)}
              alt={t("uploader.previewAlt")}
              className={`w-full ${aspectClass} object-cover rounded-md border border-sand`}
              data-testid={`${testid}-preview`}
            />
          )}
          {uploading && (
            <div className="absolute inset-0 bg-worker/60 flex items-center justify-center rounded-md">
              <Loader2 className="animate-spin text-cream" size={28} />
            </div>
          )}
          {!uploading && (
            <div className="absolute top-2 right-2 flex gap-2">
              <button
                type="button"
                onClick={pick}
                className="bg-cream text-worker rounded-sm px-2 py-1 text-xs font-display font-semibold shadow hover:bg-hive"
                data-testid={`${testid}-replace`}
              >
                <Upload size={12} className="inline mr-1" /> {t("common.replace")}
              </button>
              <button
                type="button"
                onClick={clear}
                className="bg-cream text-red-700 rounded-sm px-2 py-1 text-xs font-display font-semibold shadow hover:bg-red-100"
                data-testid={`${testid}-clear`}
              >
                <X size={12} className="inline mr-1" /> {t("common.remove")}
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={pick}
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            className={`w-full ${aspectClass} rounded-md border-2 border-dashed border-sand hover:border-hive hover:bg-linen flex flex-col items-center justify-center text-smoke text-sm gap-2 transition`}
            data-testid={`${testid}-trigger`}
          >
            {uploading ? (
              <>
                <Loader2 className="animate-spin" size={22} />
                <span>{t("uploader.uploading")}</span>
              </>
            ) : (
              <>
                <ImagePlus size={22} />
                <span className="font-display font-semibold text-worker">{label || t("uploader.defaultLabel")}</span>
                <span className="text-xs text-smoke">{t("uploader.hint", { mb: maxMb })}</span>
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            className="w-full mt-2 py-2 rounded-md border border-sand hover:border-hive hover:bg-linen flex items-center justify-center gap-2 text-sm text-smoke transition sm:hidden"
            data-testid={`${testid}-camera-btn`}
          >
            <Camera size={16} />
            <span className="font-display font-semibold text-worker">{t("uploader.takePhoto")}</span>
          </button>
        </>
      )}

      {error && (
        <p className="mt-2 text-xs text-red-700 flex items-center gap-1" data-testid={`${testid}-error`}>
          <AlertCircle size={12} /> {error}
        </p>
      )}
    </div>
  );
}
